import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import BallotIcon from "@mui/icons-material/Ballot";
import DataUsageIcon from "@mui/icons-material/DataUsage";
import GroupIcon from "@mui/icons-material/Group";
import HomeIcon from "@mui/icons-material/Home";
import LaptopIcon from "@mui/icons-material/Laptop";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import SettingsIcon from "@mui/icons-material/Settings";
import { Box, CssBaseline, Theme } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import CustomThemeProvider from "components/App/CustomThemeProvider";
import Loader from "components/Loader";
import useGlobalContext from "hooks/globalData";
import SidebarLayout from "layouts/sideBarLayout";
import LoginPage from "pages/LoginPage";
import useOrganizationContext from "hooks/useOrganizationContext";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { ResponseOfString } from "services/KnowfullyAdminClient";
import authStore from "stores/authStore";
import getTheme from "styles/theme";
import { ParentClaim } from "types/claimTypes";

const App = () => {
  const isLoggedIn = authStore.useState((s) => s.isLoggedIn);
  const claims = authStore.useState((s) => s.claims);
  const { formatMessage: t } = useIntl();
  const [theme, setTheme] = useState<Theme | null>(null);
  const {
    homeUrl,
    usersUrl,
    contentUrl,
    configurationUrl,
    certificatesUrl,
    operationsUrl,
    eventsUrl,
    accreditationUrl,
    configurationService,
  } = useGlobalContext();

  const {
    data: environment,
    isLoading: isLoadingEnvironment,
  } = useQuery<ResponseOfString>("configurationService.getEnvironment", () =>
    configurationService.getEnvironment()
  );

  useEffect(() => {
    if (!isLoadingEnvironment && environment?.data) {
      const newTheme = getTheme(environment.data.toLowerCase());
      setTheme(newTheme);
    }
  }, [environment, isLoadingEnvironment]);

  useOrganizationContext(isLoggedIn);

  const items = [
    {
      eventKey: "home",
      icon: <HomeIcon />,
      label: t({ id: "menubarHome" }),
      path: homeUrl(),
    },
    {
      eventKey: "users",
      icon: <GroupIcon />,
      label: t({ id: "menubarUsers" }),
      path: usersUrl(),
      claim: ParentClaim.Users,
    },
    {
      eventKey: "content",
      icon: <LocalLibraryIcon />,
      label: t({ id: "contentLabel" }),
      path: contentUrl(),
      claim: ParentClaim.Content,
    },
    {
      eventKey: "certificates",
      icon: <AssignmentTurnedInIcon />,
      label: t({ id: "menubarCertificates" }),
      path: certificatesUrl(),
      claim: ParentClaim.Certificates,
    },
    {
      eventKey: "accreditation",
      icon: <BallotIcon />,
      label: t({ id: "menubarAccreditation" }),
      path: accreditationUrl(),
      claim: ParentClaim.Accreditation,
    },
    {
      eventKey: "configuration",
      icon: <SettingsIcon />,
      label: t({ id: "configurationLabel" }),
      path: configurationUrl(),
      claim: ParentClaim.Configuration,
    },
    {
      eventKey: "events",
      icon: <LaptopIcon />,
      label: t({ id: "menubarEvents" }),
      path: eventsUrl(),
      claim: ParentClaim.Events,
    },
    {
      eventKey: "operations",
      icon: <DataUsageIcon />,
      label: t({ id: "operationsLabel" }),
      path: operationsUrl(),
      claim: ParentClaim.Operations,
    },
  ];

  if (!isLoggedIn) {
    return <LoginPage />;
  }

  if (isLoadingEnvironment) {
    return <Loader />;
  }

  const filteredItems = items.filter(
    (x) =>
      !x.claim ||
      claims?.some((c) => c?.parentClaim === x.claim)
  );

  return (
    !isLoadingEnvironment &&
    theme && (
      <Box sx={{ displayPrint: "none" }}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <CustomThemeProvider>
            <SidebarLayout items={filteredItems} />
          </CustomThemeProvider>
        </ThemeProvider>
      </Box>
    )
  );
};

export default App;
